import { useState } from "react";

const VEHICLES = ["Transporter", "3,5 t", "7,5 t", "LKW", "Sattelzug", "sonstiges"];
const FLEX = ["hoch", "mittel", "gering"];
const CARGO = ["Umzugsgut", "Möbel", "Einzelstück", "Kartons", "Haushaltsgeräte", "Sonstiges"];

const EMPTY = {
  start_plz: "", start_ort: "", ziel_plz: "", ziel_ort: "", abfahrt: "", ankunft: "", stops: [],
  vehicle_type: "Transporter", gesamt_volumen: "", belegt_m3: "", max_umweg_km: 30,
  preis_pro_m3: "", flexibility: "mittel", availability_from: "", availability_to: "",
  cargo_types: ["Umzugsgut", "Möbel", "Kartons"], recurring: false, recurring_weekdays: [], recurring_until: "", notes: "", status: "aktiv",
};

export default function TripForm({ initial, onSubmit, onCancel, submitLabel = "Fahrt veröffentlichen" }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const freie = (parseFloat(f.gesamt_volumen) || 0) - (parseFloat(f.belegt_m3) || 0);

  const submit = () => {
    onSubmit({
      ...f,
      gesamt_volumen: parseFloat(f.gesamt_volumen) || 0,
      belegt_m3: parseFloat(f.belegt_m3) || 0,
      freie_m3: Math.max(0, freie),
      max_umweg_km: parseFloat(f.max_umweg_km) || 0,
      preis_pro_m3: f.preis_pro_m3 ? parseFloat(f.preis_pro_m3) : null,
      recurring_until: f.recurring_until || null,
      stops: (f.stops || []).filter((s) => s.ort?.trim()),
    });
  };

  const toggleCargo = (c) => setF({ ...f, cargo_types: f.cargo_types.includes(c) ? f.cargo_types.filter(x=>x!==c) : [...f.cargo_types, c] });

  return (
    <div className="space-y-5" data-testid="trip-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="eb-label">Start-PLZ</label><input className="eb-input mt-1.5" value={f.start_plz} onChange={set("start_plz")} data-testid="trip-start-plz" /></div>
        <div><label className="eb-label">Start-Ort</label><input className="eb-input mt-1.5" value={f.start_ort} onChange={set("start_ort")} data-testid="trip-start-ort" /></div>
        <div><label className="eb-label">Ziel-PLZ</label><input className="eb-input mt-1.5" value={f.ziel_plz} onChange={set("ziel_plz")} data-testid="trip-ziel-plz" /></div>
        <div><label className="eb-label">Ziel-Ort</label><input className="eb-input mt-1.5" value={f.ziel_ort} onChange={set("ziel_ort")} data-testid="trip-ziel-ort" /></div>
        <div className="sm:col-span-2 lg:col-span-4" data-testid="trip-stops">
          <label className="eb-label">Zwischenstopps (optional, in Reihenfolge – z. B. Köln → Frankfurt → Nürnberg → München)</label>
          <div className="space-y-2 mt-1.5">
            {f.stops.map((s, i) => (
              <div key={i} className="flex gap-2 items-center">
                <span className="text-xs text-slate-500 w-5">{i + 1}.</span>
                <input className="eb-input !w-28" placeholder="PLZ" value={s.plz} onChange={(e) => setF({ ...f, stops: f.stops.map((x, k) => k === i ? { ...x, plz: e.target.value } : x) })} data-testid={`trip-stop-plz-${i}`} />
                <input className="eb-input flex-1" placeholder="Ort" value={s.ort} onChange={(e) => setF({ ...f, stops: f.stops.map((x, k) => k === i ? { ...x, ort: e.target.value } : x) })} data-testid={`trip-stop-ort-${i}`} />
                <button type="button" onClick={() => setF({ ...f, stops: f.stops.filter((_, k) => k !== i) })} className="eb-btn-ghost !p-2 text-red-300" data-testid={`trip-stop-remove-${i}`}>✕</button>
              </div>
            ))}
            {f.stops.length < 6 && <button type="button" onClick={() => setF({ ...f, stops: [...f.stops, { plz: "", ort: "" }] })} className="eb-btn-outline !py-1.5 text-xs" data-testid="trip-stop-add">+ Zwischenstopp</button>}
          </div>
        </div>
        <div><label className="eb-label">Abfahrt</label><input type="date" className="eb-input mt-1.5" value={f.abfahrt?.slice(0,10)} onChange={set("abfahrt")} data-testid="trip-abfahrt" /></div>
        <div><label className="eb-label">Voraussichtliche Ankunft</label><input type="date" className="eb-input mt-1.5" value={f.ankunft?.slice(0,10)} onChange={set("ankunft")} data-testid="trip-ankunft" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="eb-label">Fahrzeug</label><select className="eb-input mt-1.5" value={f.vehicle_type} onChange={set("vehicle_type")} data-testid="trip-vehicle">{VEHICLES.map(v=><option key={v}>{v}</option>)}</select></div>
        <div><label className="eb-label">Flexibilität</label><select className="eb-input mt-1.5" value={f.flexibility} onChange={set("flexibility")} data-testid="trip-flex">{FLEX.map(v=><option key={v}>{v}</option>)}</select></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div><label className="eb-label">Gesamt m³</label><input type="number" className="eb-input mt-1.5" value={f.gesamt_volumen} onChange={set("gesamt_volumen")} data-testid="trip-gesamt" /></div>
        <div><label className="eb-label">Belegt m³</label><input type="number" className="eb-input mt-1.5" value={f.belegt_m3} onChange={set("belegt_m3")} data-testid="trip-belegt" /></div>
        <div><label className="eb-label">Frei m³</label><div className="eb-input mt-1.5 mono text-emerald-400 bg-[#111827]">{Math.max(0, freie)}</div></div>
        <div><label className="eb-label">Max. Umweg km</label><input type="number" className="eb-input mt-1.5" value={f.max_umweg_km} onChange={set("max_umweg_km")} data-testid="trip-umweg" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="eb-label">Preis pro m³ (optional)</label><input type="number" className="eb-input mt-1.5" value={f.preis_pro_m3} onChange={set("preis_pro_m3")} data-testid="trip-preis" /></div>
        <div><label className="eb-label">Verfügbarkeit von / bis</label>
          <div className="flex gap-2 mt-1.5">
            <input type="date" className="eb-input" value={f.availability_from?.slice(0,10)} onChange={set("availability_from")} data-testid="trip-avail-from" />
            <input type="date" className="eb-input" value={f.availability_to?.slice(0,10)} onChange={set("availability_to")} data-testid="trip-avail-to" />
          </div>
        </div>
      </div>
      <div>
        <label className="eb-label">Welche Güter können mitgenommen werden?</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {CARGO.map((c) => (
            <button key={c} type="button" onClick={() => toggleCargo(c)}
              className={`px-3 py-1.5 rounded-full text-xs border ${f.cargo_types.includes(c)?"border-[#FF5A1F] bg-[#FF5A1F]/10 text-white":"border-slate-700 text-slate-400"}`}>{c}</button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
        <input type="checkbox" checked={f.recurring} onChange={set("recurring")} className="w-4 h-4 accent-[#FF5A1F]" data-testid="trip-recurring" />
        Wöchentlich wiederholen (Fahrten werden automatisch 4 Wochen im Voraus angelegt)
      </label>
      {f.recurring && (
        <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-4 grid sm:grid-cols-2 gap-4" data-testid="trip-recurrence">
          <div>
            <label className="eb-label">Wochentage</label>
            <div className="flex gap-1.5 mt-2">
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d, i) => (
                <button key={d} type="button" onClick={() => setF({ ...f, recurring_weekdays: f.recurring_weekdays.includes(i) ? f.recurring_weekdays.filter((x) => x !== i) : [...f.recurring_weekdays, i].sort() })}
                  data-testid={`trip-weekday-${i}`}
                  className={`w-10 h-9 rounded-md text-xs border ${f.recurring_weekdays.includes(i) ? "border-[#FF5A1F] bg-[#FF5A1F]/15 text-white" : "border-slate-700 text-slate-400"}`}>{d}</button>
              ))}
            </div>
          </div>
          <div><label className="eb-label">Wiederholen bis (max. 6 Monate)</label><input type="date" className="eb-input mt-1.5" value={f.recurring_until?.slice(0, 10) || ""} onChange={set("recurring_until")} data-testid="trip-recurring-until" /></div>
        </div>
      )}
      <div className="flex gap-2 pt-2">
        {onCancel && <button onClick={onCancel} className="eb-btn-outline">Abbrechen</button>}
        <button onClick={submit} className="eb-btn-primary" data-testid="trip-submit">{submitLabel}</button>
      </div>
    </div>
  );
}
